import { ArrowRight, ChevronDown } from 'lucide-react';
import * as React from 'react';
import { Callout } from '@/components/patterns/callout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { dataSource } from '@/data';
import { mensagemDoErro } from '@/data/http';
import type { ArtefatoAlterado, ArtefatoDiff, BuildResumo, DiffAnterior } from '@/data/types';
import { useResource } from '@/hooks/use-resource';
import { plural } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * "O que mudou" de uma publicação: resumo curto sempre visível e, ao clicar em
 * "Ver mudanças", a lista compacta de artefatos alterados/adicionados/removidos
 * contra a versão anterior. O detalhe da versão (que traz o diff) só é buscado
 * ao abrir pela primeira vez — a API do feed não muda.
 */
export function OQueMudou({ build }: { build: BuildResumo }) {
  const [aberto, setAberto] = React.useState(false);
  const [jaAbriu, setJaAbriu] = React.useState(false);
  const painelId = `mudancas-${build.id}`;

  function alternar() {
    setAberto((valor) => !valor);
    setJaAbriu(true);
  }

  return (
    <div className="rounded-input border border-border-subtle bg-bg-subtle">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
        <span className="cmp-eyebrow">O que mudou</span>
        <span className="text-cell text-on-surface-muted">{plural(build.total_artefatos, 'artefato', 'artefatos')} no manifesto</span>
        <Button
          variant="ghost"
          size="sm"
          className="-my-1 ml-auto h-7 px-2 text-primary hover:bg-primary-wash"
          aria-expanded={aberto}
          aria-controls={painelId}
          onClick={alternar}
        >
          {aberto ? 'Ocultar mudanças' : 'Ver mudanças'}
          <ChevronDown className={cn('transition-transform duration-fast ease-standard', aberto && 'rotate-180')} aria-hidden="true" />
        </Button>
      </div>
      <div id={painelId} hidden={!aberto} className="border-t border-border-subtle px-3 py-3">
        {jaAbriu ? <MudancasConteudo build={build} /> : null}
      </div>
    </div>
  );
}

function MudancasConteudo({ build }: { build: BuildResumo }) {
  const recurso = useResource((signal) => dataSource.obterBuild(build.versao, build.produto, signal), [build.versao, build.produto]);

  if (recurso.estado === 'erro') {
    return (
      <Callout
        tom="erro"
        acao={
          <Button size="sm" variant="outline" onClick={recurso.recarregar}>
            Tentar de novo
          </Button>
        }
      >
        Não foi possível carregar as mudanças de {build.versao}:{' '}
        {mensagemDoErro(recurso.erro) ?? recurso.erro?.message ?? 'erro desconhecido'}
      </Callout>
    );
  }
  if (!recurso.dados) {
    return (
      <div className="flex flex-col gap-2" role="status" aria-label="Carregando as mudanças">
        <Skeleton className="h-4 w-64 max-w-full" />
        <Skeleton className="h-6" />
        <Skeleton className="h-6" />
        <Skeleton className="h-6 w-2/3" />
      </div>
    );
  }
  return <ListaMudancas diff={recurso.dados.diff_anterior} />;
}

const LIMITE = 6;

function ListaMudancas({ diff }: { diff: DiffAnterior }) {
  const total = diff.alterados.length + diff.adicionados.length + diff.removidos.length;

  if (diff.versao_anterior === null) {
    return <p className="text-cell text-on-surface-muted">Primeira versão deste branch: não há versão anterior para comparar.</p>;
  }
  if (total === 0) {
    return (
      <p className="text-cell text-on-surface-muted">
        Nenhum artefato mudou em relação à <span className="font-mono text-on-surface">{diff.versao_anterior}</span>.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-cell text-on-surface-muted">
        Comparado a <span className="font-mono text-on-surface">{diff.versao_anterior}</span>:{' '}
        {plural(diff.alterados.length, 'alterado', 'alterados')} · {plural(diff.adicionados.length, 'adicionado', 'adicionados')} ·{' '}
        {plural(diff.removidos.length, 'removido', 'removidos')}
      </p>
      <Grupo titulo="Alterados" tom="text-warning-text" itens={diff.alterados} linha={(item) => <LinhaAlterado item={item} />} />
      <Grupo titulo="Adicionados" tom="text-success" itens={diff.adicionados} linha={(item) => <LinhaSimples item={item} sinal="+" />} />
      <Grupo titulo="Removidos" tom="text-danger" itens={diff.removidos} linha={(item) => <LinhaSimples item={item} sinal="−" />} />
    </div>
  );
}

function Grupo<T extends { artefato: string; tipo: string }>({
  titulo,
  tom,
  itens,
  linha,
}: {
  titulo: string;
  tom: string;
  itens: T[];
  linha: (item: T) => React.ReactNode;
}) {
  const [todos, setTodos] = React.useState(false);
  if (itens.length === 0) return null;
  const visiveis = todos ? itens : itens.slice(0, LIMITE);
  return (
    <section aria-label={`${titulo} (${itens.length})`} className="flex flex-col gap-1">
      <h4 className={cn('text-label', tom)}>
        {titulo} <span className="font-normal text-on-surface-muted">({itens.length})</span>
      </h4>
      <ul className="flex flex-col divide-y divide-border-subtle rounded-input border border-border-subtle bg-bg-surface">
        {visiveis.map((item) => (
          <li key={`${item.artefato}-${item.tipo}`} className="px-2.5 py-1.5">
            {linha(item)}
          </li>
        ))}
      </ul>
      {itens.length > LIMITE ? (
        <button
          type="button"
          onClick={() => setTodos((valor) => !valor)}
          className="w-fit rounded-button text-label text-primary underline-offset-4 hover:underline"
        >
          {todos ? 'Mostrar menos' : `Mostrar todos os ${itens.length}`}
        </button>
      ) : null}
    </section>
  );
}

function LinhaAlterado({ item }: { item: ArtefatoAlterado }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-cell">
      <span className="min-w-0 break-all font-bold">{item.artefato}</span>
      <span className="inline-flex flex-wrap items-center gap-1.5 font-mono text-on-surface-muted">
        {item.de}
        <ArrowRight className="size-3 text-icon" aria-label="para" />
        <span className="text-on-surface">{item.para}</span>
      </span>
    </div>
  );
}

function LinhaSimples({ item, sinal }: { item: ArtefatoDiff; sinal: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-cell">
      <span className="min-w-0 break-all">
        <span aria-hidden="true" className="mr-1.5 font-mono text-on-surface-muted">
          {sinal}
        </span>
        <span className="font-bold">{item.artefato}</span>
      </span>
      <span className="font-mono text-on-surface-muted">{item.versao}</span>
    </div>
  );
}
